export function compoundRate(rate: number, numPeriods: number): number {
    return Math.pow(1 + rate, numPeriods) - 1;
}

export function geometricMean(percent: number, divisor: number): number {
    return compoundRate(percent, 1 / divisor);
}

export interface PVParams {
    futureBalance: number;
    /**
     * Percent growth-per-period, as decimal.
     * Ex: 5% = 0.05
     */
    interestRate: number;
    numPeriods: number;
}

/** 
    Future value function

    Formula: PV = FV * (1 / (1+r)^n)
*/
export function PV({
    futureBalance: fv,
    interestRate: r,
    numPeriods: n,
}: PVParams): number {
    return fv * (1 / Math.pow(1 + r, n));
}
export interface FVParams {
    startingBalance: number;
    /**
     * Percent growth-per-period, as decimal.
     * Ex: 5% = 0.05
     */
    interestRate: number;
    numPeriods: number;
}

/** 
    Future value function

    Formula: FV = PV * (1+r)^n
*/
export function FV({
    startingBalance: pv,
    interestRate: r,
    numPeriods: n,
}: FVParams): number {
    return pv * Math.pow(1 + r, n);
}

export interface FVAnnuityParams {
    payment: number; // dollar amount, in cents
    interestRate: number; // decimal, per period
    numPeriods: number; // number of periods
}

/** 
    Future value of an Annuity

    Formula: FV = P * (((1+r)^n - 1) / r)
*/
export function FVAnnuity({
    payment: p,
    interestRate: r,
    numPeriods: n,
}: FVAnnuityParams) {
    if (r === 0) return p * n;

    return p * ((Math.pow(1 + r, n) - 1) / r);
}

export function FVAnnuityDue({
    payment: p,
    interestRate: r,
    numPeriods: n,
}: FVAnnuityParams): number {
    if (r === 0) return p * n;

    return p * (1 + r) * ((Math.pow(1 + r, n) - 1) / r);
}

export interface PmtAnnuityParams {
    futureValue: number; // dollar amount, in cents
    interestRate: number; // decimal, per period
    numPeriods: number; // number of periods
}

/** 
    Annuity payment from future value

    Formula: P = (FV * r) / ((1+r)^n - 1)
*/
export function PmtAnnuity({
    futureValue: fv,
    interestRate: r,
    numPeriods: n,
}: PmtAnnuityParams): number {
    if (r === 0) return fv / n;
    if (n === 0) return fv;

    return (fv * r) / (Math.pow(1 + r, n) - 1);
}

/** 
    Annuity due payment from future value

    Formula: P = (FV * r) / (((1+r)^n - 1) * (1+r))
*/
export function PmtAnnuityDue({
    futureValue: fv,
    interestRate: r,
    numPeriods: n,
}: PmtAnnuityParams): number {
    if (r === 0) return fv / n;
    if (n === 0) return fv;

    return (fv * r) / ((Math.pow(1 + r, n) - 1) * (1 + r));
}
